import * as THREE from 'three/webgpu';
import type { FeatureResultMesh } from './FeatureResultMesh';
import { PointsMaterialFactory } from '../../material';
import type { CameraSpriteNodeMaterial } from '../../material';
import type { FeaturePointMaterialState } from '../../../types';

/** 特征点默认命名计数器。 */
let featurePointNameCounter = 1;

/**
 * 判断对象是否为特征顶点对象。
 *
 * @param value 待判断对象。
 * @returns 是否为特征顶点对象。
 */
export function isFeaturePoint(value: unknown): value is FeaturePoint {
  if (!value || typeof value !== 'object') {
    return false;
  }
  return Reflect.get(value, 'isFeaturePoint') === true;
}

/**
 * 特征顶点对象。
 *
 * 设计说明：
 * 1. development 分支已将点渲染统一迁移到 `THREE.Sprite` 语义；
 * 2. 当前分支新增的特征顶点对象也必须沿用同一渲染链，避免草图点与特征点分裂成两套实现；
 * 3. 通过显式成员保存顶点索引与元数据，不依赖隐式运行时附加字段。
 */
export class FeaturePoint extends THREE.Sprite {
  /** 运行时类型标记。 */
  public readonly isFeaturePoint: boolean = true;

  /** 所属结果体对象 ID。 */
  public objectId: string | null = null;

  /** 顶点索引。 */
  public vertexIndex: number | null = null;

  /** 权威子拓扑 ID。 */
  public subEntityId: string | null = null;

  /** 持久化点引用。 */
  public persistentRef: string | null = null;

  /** 特征元数据。 */
  private featureMetadata: Record<string, unknown> = {};

  /** 所属权威结果体。*/
  private owningResultMesh: FeatureResultMesh | null = null;

  /** 当前代理独占的有限状态材质；跨帧切换复用，移除代理时统一释放。 */
  private readonly stateMaterials = new Map<FeaturePointMaterialState, CameraSpriteNodeMaterial>();

  /** 防止宿主清理与显式销毁重复派发材质释放事件。 */
  private disposed = false;

  /**
   * 构造函数。
   *
   * @param position 顶点局部坐标。
   */
  public constructor(position: THREE.Vector3) {
    // 从配方创建节点图，不克隆相机订阅；材质生命周期与短期拓扑代理一致。
    const material = PointsMaterialFactory.createOwnedFeaturePointSpriteMaterial('default');
    super(material);
    this.stateMaterials.set('default', material);
    this.position.copy(position);
    this.scale.setScalar(0.01);
    this.name = `点${featurePointNameCounter}`;
    featurePointNameCounter += 1;
    this.renderOrder = 3;
    this.frustumCulled = false;
    this.visible = false;
  }

  /**
   * 取得当前点独占的状态材质，重复交互不分配新材质。
   * @param state 默认、移入或选中配方。
   * @returns 随本点统一释放的材质，调用者只负责赋值，不单独销毁。
   */
  public getInteractionMaterial(state: FeaturePointMaterialState): CameraSpriteNodeMaterial {
    if (this.disposed) {
      throw new Error(`无法获取已释放拓扑点的交互材质：${this.uuid}`);
    }
    const cached = this.stateMaterials.get(state);
    if (cached) {
      return cached;
    }
    // 默认材质始终与点同寿命，即使工厂缓存被清空，也能提供当前相机投影状态。
    const material = PointsMaterialFactory.createOwnedFeaturePointSpriteMaterial(
      state, this.stateMaterials.get('default')!.isPerspectiveCameraUniform.value,
    );
    this.stateMaterials.set(state, material);
    return material;
  }

  /**
   * 释放该点曾使用的全部材质与相机订阅，结束对应 RenderObject 的绑定。
   * Sprite 的四边形由 Three.js 全局共享，不能在单个拓扑点退出时释放。
   */
  public dispose(): void {
    if (this.disposed) {
      return;
    }
    this.disposed = true;
    this.removeFromParent();
    this.owningResultMesh = null;
    this.featureMetadata = {};
    this.stateMaterials.forEach(material => material.dispose());
    this.stateMaterials.clear();
  }

  /**
   * 设置特征顶点语义上下文。
   *
   * @param input 上下文参数。
   */
  public setFeatureContext(input: {
    objectId: string;
    metadata: Record<string, unknown>;
    vertexIndex: number;
    subEntityId: string;
    persistentRef: string;
  }): void {
    this.objectId = input.objectId;
    this.vertexIndex = input.vertexIndex;
    this.subEntityId = input.subEntityId;
    this.persistentRef = input.persistentRef;
    this.featureMetadata = {
      ...input.metadata,
    };
  }

  /**
   * 读取特征元数据副本。
   *
   * @returns 元数据副本。
   */
  public getFeatureMetadata(): Record<string, unknown> {
    return {
      ...this.featureMetadata,
    };
  }

  //------ 参考平面几何读取封装 开始------
  /**
   * 读取特征点世界坐标。
   *
   * 参考平面求解只关心点的世界位置，因此由特征点对象直接暴露统一读取入口，
   * 避免上层工具函数重复创建相同的读取逻辑。
   *
   * @returns 世界坐标。
   */
  public getWorldPoint(): THREE.Vector3 {
    const point = new THREE.Vector3();
    this.getWorldPosition(point);
    return point;
  }
  //------ 参考平面几何读取封装 结束------

  /**
   * 设置当前顶点代理所属的权威结果体。
   *
   * @param mesh 所属结果体；传入 `null` 表示清空绑定。
   */
  public setOwningResultMesh(mesh: FeatureResultMesh | null): void {
    this.owningResultMesh = mesh;
  }

  /**
   * 读取当前顶点代理所属的权威结果体。
   *
   * @returns 所属结果体；未绑定时返回 `null`。
   */
  public getOwningResultMesh(): FeatureResultMesh | null {
    return this.owningResultMesh;
  }
}
